import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, AlertTriangle, Loader2, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";

const MAX_ATTEMPTS = 6;
const POLL_INTERVAL_MS = 2500;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("polling"); // polling | paid | failed | expired
  const [tx, setTx] = useState(null);
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState("failed");
      return;
    }

    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        setTx(data);
        if (data.payment_status === "paid") {
          setState("paid");
          return;
        }
        if (data.status === "expired") {
          setState("expired");
          return;
        }
        attemptsRef.current += 1;
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          setState("failed");
          return;
        }
        setTimeout(poll, POLL_INTERVAL_MS);
      } catch (e) {
        console.error(e);
        if (!cancelled) {
          attemptsRef.current += 1;
          if (attemptsRef.current >= MAX_ATTEMPTS) setState("failed");
          else setTimeout(poll, POLL_INTERVAL_MS);
        }
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  return (
    <div data-testid="page-payment-success">
      <section className="relative py-32 overflow-hidden">
        <div className="absolute inset-0 eq-grid-bg" />
        <div className="eq-aurora" />
        <div className="relative max-w-2xl mx-auto px-6 md:px-10 text-center">
          {state === "polling" && (
            <>
              <div className="w-16 h-16 mx-auto rounded-2xl bg-blue-500/10 border border-blue-500/40 grid place-items-center text-blue-300">
                <Loader2 className="w-7 h-7 animate-spin" strokeWidth={1.6} />
              </div>
              <h1 className="mt-8 font-display text-4xl font-semibold">
                Verifying payment…
              </h1>
              <p className="mt-4 text-gray-400">Please don't close this page.</p>
            </>
          )}
          {state === "paid" && (
            <>
              <div className="w-16 h-16 mx-auto rounded-2xl bg-blue-500/10 border border-blue-500/40 grid place-items-center text-blue-300 shadow-[0_0_40px_rgba(37,99,235,0.4)]">
                <CheckCircle2 className="w-8 h-8" strokeWidth={1.6} />
              </div>
              <h1 className="mt-8 font-display text-5xl font-semibold">Payment received.</h1>
              <p className="mt-5 text-gray-400 leading-relaxed">
                Thanks! Your{" "}
                <span className="text-white">
                  {tx?.metadata?.label || "Equinox Systems"}
                </span>{" "}
                payment of{" "}
                <span className="text-white">
                  ${(tx?.amount_total ? tx.amount_total / 100 : tx?.amount || 0).toFixed(2)}{" "}
                  {(tx?.currency || "USD").toUpperCase()}
                </span>{" "}
                was successful. We'll be in touch with next steps shortly.
              </p>
              <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
                <Link to="/" className="btn-primary" data-testid="payment-home-btn">
                  Back to Home <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/portfolio" className="btn-ghost" data-testid="payment-portfolio-btn">
                  See Our Work
                </Link>
              </div>
            </>
          )}
          {(state === "failed" || state === "expired") && (
            <>
              <div className="w-16 h-16 mx-auto rounded-2xl bg-red-500/10 border border-red-500/40 grid place-items-center text-red-300">
                <AlertTriangle className="w-7 h-7" strokeWidth={1.6} />
              </div>
              <h1 className="mt-8 font-display text-4xl font-semibold">
                Payment status unclear
              </h1>
              <p className="mt-4 text-gray-400">
                We couldn't confirm your payment automatically. Please contact us
                and we'll resolve this immediately.
              </p>
              <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
                <Link to="/packages" className="btn-primary" data-testid="payment-retry-btn">
                  Back to Packages
                </Link>
                <Link to="/contact" className="btn-ghost" data-testid="payment-contact-btn">
                  Contact Support
                </Link>
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
}
